import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

// Signed-in account actions + per-store UI preferences, provided by App so
// deep components (profile, team, settings…) don't need prop drilling.
export const StoreContext = createContext({
  user: null,
  prefs: {},
  setPref: () => {},
  updateProfile: async () => {},
  changePassword: async () => {},
  changeTier: async () => {},
  cancelPlan: async () => {},
  openTicket: async () => {},
});

export const useStore = () => useContext(StoreContext);

// useState-like hook backed by the store's saved preferences. Writes are
// debounced so typing into a field doesn't send a request per keystroke.
export function usePref(key, initial) {
  const { prefs, setPref } = useStore();
  const value = prefs && Object.prototype.hasOwnProperty.call(prefs, key) ? prefs[key] : initial;
  const latest = useRef(value);
  latest.current = value;
  const set = useCallback(next => {
    const v = typeof next === 'function' ? next(latest.current) : next;
    latest.current = v;
    setPref(key, v);
  }, [key, setPref]);
  return [value, set];
}

// Debounce helper for saves keyed by name
export function useDebouncedSaver(delay = 500) {
  const timers = useRef({});
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  return useCallback((name, fn) => {
    clearTimeout(timers.current[name]);
    timers.current[name] = setTimeout(fn, delay);
  }, [delay]);
}
